/**
 * serverBootstrap — reads the data the page was rendered with.
 *
 * `/users/edit/` already ships everything this tab needs inside
 * `window.serverData` (see `views/users/users-edit.twig` `{% block JSON %}`
 * → `abstracts/layout.twig:155`): the user record, the country list and the
 * state list. No request is made on mount — the MVP renders from the same
 * payload the legacy AngularJS page consumed.
 *
 * Saving already goes through Symfony (`PUT /user/profile`, UserProfilePage).
 * Reading could too (`GET /user/profile`): replace `readUser()` with a fetch
 * here and nothing above it changes.
 */

const EMPTY = {};

/** Raw `window.serverData`, or an empty object when rendered outside the shell. */
export function serverData() {
	return (typeof window !== 'undefined' && window.serverData) || EMPTY;
}

const USER_CACHE_KEY = 'hs.userProfile.identity';

/**
 * The user record the page was rendered for, with the name keys normalised.
 *
 * The two routes do not agree on shape: `/users/edit/` runs the row through
 * `Users::editUser()`, which maps `name`/`surname` to `firstName`/`lastName`,
 * while `/users/documents/` puts the raw DB row on the page — and the `users`
 * table columns are `name` and `surname`. Reading only one pair left the
 * documents tab rendering "Unnamed user".
 *
 * Writing still uses `firstName`/`lastName`: those are the POST field names
 * the PHP action expects, independent of the column names.
 */
export function readUser(src) {
	const u = (src || serverData()).user;
	if (!u || typeof u !== 'object') return EMPTY;
	return {
		...u,
		firstName: u.firstName != null && u.firstName !== '' ? u.firstName : u.name || '',
		lastName: u.lastName != null && u.lastName !== '' ? u.lastName : u.surname || ''
	};
}

/**
 * The identity the left rail shows, carried across tabs.
 *
 * The five tabs are five server routes, so each is a full page load and the
 * rail is rebuilt every time. It cannot literally persist without collapsing
 * them into one client-side surface — but the DATA can, and that is what
 * actually shows: the rail is stable and complete on every tab instead of
 * thinning out wherever a route ships a leaner user row (documents ships no
 * `countryName`, for one).
 *
 * The snapshot is merged, never trusted wholesale: this page's own record
 * always wins field by field, so a save is reflected immediately and the cache
 * only fills gaps. Session-scoped, so it dies with the tab.
 */
export function readIdentity(src) {
	const current = readUser(src);
	if (typeof sessionStorage === 'undefined') return current;

	let cached = EMPTY;
	try {
		cached = JSON.parse(sessionStorage.getItem(USER_CACHE_KEY) || '{}') || EMPTY;
	} catch (e) {
		cached = EMPTY;
	}

	// A cache belonging to a different user must never leak into this page.
	const sameUser =
		cached.id != null && current.id != null && String(cached.id) === String(current.id);
	const merged = sameUser ? { ...cached, ...pruneEmpty(current) } : current;

	try {
		if (merged.id != null) sessionStorage.setItem(USER_CACHE_KEY, JSON.stringify(merged));
	} catch (e) {
		/* storage full or blocked — the rail still renders from `current` */
	}
	return merged;
}

/** Drops empty values so a leaner page cannot blank a field the cache has. */
function pruneEmpty(obj) {
	const out = {};
	Object.keys(obj).forEach(k => {
		const v = obj[k];
		if (v !== '' && v !== null && v !== undefined) out[k] = v;
	});
	return out;
}

/**
 * Countries as `{ id, name }`. The legacy payload is a map keyed by id in
 * some deployments and a list in others, so both shapes are normalised here
 * rather than in the component.
 */
export function readCountries(src) {
	return normalizeOptions((src || serverData()).allCountry);
}

/** States as `{ id, name, countryId }`. Same dual-shape handling. */
export function readStates(src) {
	const raw = (src || serverData()).states;
	return normalizeOptions(raw).map(o => ({
		...o,
		countryId:
			o.countryID != null ? String(o.countryID) : o.countryId != null ? String(o.countryId) : null
	}));
}

function normalizeOptions(raw) {
	if (Array.isArray(raw)) return raw.filter(Boolean).map(toOption);
	if (raw && typeof raw === 'object') {
		return Object.keys(raw)
			.map(key => {
				const v = raw[key];
				if (v == null) return null;
				if (typeof v === 'string') return { id: String(key), name: v };
				return toOption({ id: v.id != null ? v.id : key, ...v });
			})
			.filter(Boolean);
	}
	return [];
}

function toOption(v) {
	if (v == null) return null;
	if (typeof v === 'string') return { id: v, name: v };
	return {
		...v,
		id: v.id != null ? String(v.id) : '',
		name: v.name != null ? v.name : v.title != null ? v.title : String(v.id != null ? v.id : '')
	};
}
